import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsHighPrecision } from '../../custom-type'
import { organization } from '../auth'
import { File } from '../file'
import { RoyaltyTransaction } from './royalty-transaction'

export const RoyaltyWithdrawal = pgTable(
  'royalty_withdrawal',
  {
    id: uuid().primaryKey().defaultRandom(),
    organizationId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'restrict' }),
    amount: numericAsHighPrecision().notNull(),
    currency: text().notNull().default('IDR'),
    status: text({ enum: ['PENDING', 'PAID', 'CANCELLED'] })
      .notNull()
      .default('PENDING'),
    requestedAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    paidAt: timestamp({ mode: 'date', withTimezone: true }),
    cancelledAt: timestamp({ mode: 'date', withTimezone: true }),
    receiptFileId: uuid().references(() => File.id, { onDelete: 'restrict' }),
    notes: text(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    index('royalty_withdrawal_organization_idx').on(t.organizationId),
    index('royalty_withdrawal_status_idx').on(t.status),
    index('royalty_withdrawal_requested_at_idx').on(t.requestedAt),
  ],
)

export const royaltyWithdrawalRelations = relations(RoyaltyWithdrawal, ({ one, many }) => ({
  organization: one(organization, {
    fields: [RoyaltyWithdrawal.organizationId],
    references: [organization.id],
  }),
  transactions: many(RoyaltyTransaction),
  receiptFile: one(File, {
    fields: [RoyaltyWithdrawal.receiptFileId],
    references: [File.id],
  }),
}))

export const InsertRoyaltyWithdrawalSchema = createInsertSchema(RoyaltyWithdrawal).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateRoyaltyWithdrawalSchema = createUpdateSchema(RoyaltyWithdrawal).partial().omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
})

export const SelectRoyaltyWithdrawalSchema = createSelectSchema(RoyaltyWithdrawal)

export type InsertRoyaltyWithdrawal = typeof RoyaltyWithdrawal.$inferInsert
export type UpdateRoyaltyWithdrawal = z.infer<typeof UpdateRoyaltyWithdrawalSchema>
export type SelectRoyaltyWithdrawal = typeof RoyaltyWithdrawal.$inferSelect
